import { ButtonView as Button } from 'interfaces/graphqlViews';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RequestPolicy } from 'urql';
import useIsQuotesQueryPaused from 'hooks/useIsQuotesQueryPaused';
import useSearch from 'hooks/useSearch';
import {
  CartTotalsFragment,
  FormFragment as Form,
  InputsProductFragment,
  PaymentMethod,
  QuoteCheckoutScreenQuery,
  QuoteCheckoutStatus,
  useQuoteCheckoutScreenQuery,
} from 'generated/graphql';

// The context carries what the quote checkout query returns; the generated
// schema types nest recursive actions no query result can satisfy.
type QuoteCheckoutData = NonNullable<QuoteCheckoutScreenQuery['quoteCheckout']>;

interface InputsCheckoutContextProps {
  checkoutDetails?: QuoteCheckoutData['checkoutDetails'];
  isFetching: boolean;
  quoteID: string;
  hasQuoteID: boolean;
  isConfirmation: boolean;
  numberOfProducts: number;
  arriveByInfo?: Button;
  products: InputsProductFragment[];
  totals: CartTotalsFragment[];
  orderNumber: number;
  afterPurchaseCreditBalance: number;
  creditBalance: number;
  confirmationInfo?: QuoteCheckoutData['checkoutConfirmation'];
  onRefreshQuotesCheckout: (requestPolicy?: RequestPolicy | undefined) => void;
  hasCreditPaymentMethod: boolean;
  paymentMethod: PaymentMethod[];
  setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod[]>>;
  purchaseOrderNo: string;
  setPurchaseOrderNo: React.Dispatch<React.SetStateAction<string>>;
  popFiles: string[];
  setPopFiles: React.Dispatch<React.SetStateAction<string[]>>;
  form?: Form;
  setForm: React.Dispatch<React.SetStateAction<Form | undefined>>;
}

export const Context = createContext<InputsCheckoutContextProps | undefined>(undefined);

export const InputsCheckoutProvider = ({ children }: { children: ReactNode }) => {
  const isQuotesQueryPaused = useIsQuotesQueryPaused();
  const [getParam] = useSearch();
  const quoteID = getParam('quoteID') ?? '0';

  const [popFiles, setPopFiles] = useState<string[]>([]);
  const [purchaseOrderNo, setPurchaseOrderNo] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const [form, setForm] = useState<Form>();

  const [quoteCheckoutResult, refreshQuoteCheckoutResult] = useQuoteCheckoutScreenQuery({
    variables: {
      quoteID,
    },
    pause: isQuotesQueryPaused,
  });

  const { data, fetching: isFetching, error } = quoteCheckoutResult;

  const checkoutDetails = data?.quoteCheckout?.checkoutDetails;
  const checkoutSummary = data?.quoteCheckout?.checkoutSummary;
  const hasCreditPaymentMethod = checkoutDetails?.methods.includes(PaymentMethod.Credit) ?? false;

  useEffect(() => {
    // Set default payment method
    setPaymentMethod([hasCreditPaymentMethod ? PaymentMethod.Credit : PaymentMethod.Eft]);

    // Set proof of payment file
    const proofOfPayment = data?.quoteCheckout?.proofOfPayment;
    if (proofOfPayment) setPopFiles([proofOfPayment]);
  }, [data, hasCreditPaymentMethod, setPopFiles, setPaymentMethod]);

  const handleRefreshQuotesCheckout = useCallback(
    (requestPolicy: RequestPolicy = 'network-only') => {
      refreshQuoteCheckoutResult({ requestPolicy });
    },
    [refreshQuoteCheckoutResult],
  );

  const value = useMemo(
    () => ({
      isFetching,
      quoteID,
      hasQuoteID: quoteID !== '0',
      isConfirmation: data?.quoteCheckout.orderStatus !== QuoteCheckoutStatus.Pending, // Confirmation can be all other statues but pending
      numberOfProducts: checkoutSummary?.products.length ?? 0,
      arriveByInfo: checkoutSummary?.arriveBy,
      products: checkoutSummary?.products ?? [],
      totals: checkoutSummary?.totals ?? [],
      orderNumber: data?.quoteCheckout.orderNumber ?? 0,
      afterPurchaseCreditBalance: checkoutDetails?.afterPurchaseCreditBalance ?? 0,
      creditBalance: checkoutDetails?.creditBalance ?? 0,
      checkoutDetails: checkoutDetails,
      hasCreditPaymentMethod,
      confirmationInfo: data?.quoteCheckout?.checkoutConfirmation,
      onRefreshQuotesCheckout: handleRefreshQuotesCheckout,
      paymentMethod,
      setPaymentMethod,
      purchaseOrderNo,
      setPurchaseOrderNo,
      popFiles,
      setPopFiles,
      form,
      setForm,
    }),
    [
      data,
      quoteID,
      checkoutSummary,
      checkoutDetails,
      hasCreditPaymentMethod,
      isFetching,
      handleRefreshQuotesCheckout,
      paymentMethod,
      setPaymentMethod,
      purchaseOrderNo,
      setPurchaseOrderNo,
      popFiles,
      setPopFiles,
      form,
      setForm,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useInputsCheckout = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useInputsCheckout must be used within a InputsCheckoutProvider');
  return result;
};
